import type { Clerk } from '@clerk/clerk-js';
/**
 * Application chrome — sidebar (desktop) + topbar + mobile tabbar.
 *
 * Adapted from Wayne's Brain pwa-v2/src/components/chrome.tsx with the
 * route map narrowed to Known Accounts' current surfaces.
 *
 * The Sidebar groups nav items in three buckets to give the rail visual
 * rhythm; as more surfaces ship (Board → "Today"; Triggers → "Today"
 * or "Signals"; Coverage / Activity → "Portfolio") the groups fill in.
 */
import { useLocation } from 'preact-iso';
import { useEffect, useState } from 'preact/hooks';
import { Icons } from './icons';

type IconKey = keyof typeof Icons;

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: IconKey;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Today',
    items: [
      { id: 'briefing', label: 'Briefing', href: '/', icon: 'Briefing' },
      { id: 'capture', label: 'Capture', href: '/capture', icon: 'Plus' },
      { id: 'board', label: 'Board', href: '/board', icon: 'Board' },
    ],
  },
  {
    label: 'Accounts',
    items: [{ id: 'portfolio', label: 'Portfolio', href: '/portfolio', icon: 'Accounts' }],
  },
  {
    label: 'Find',
    items: [{ id: 'search', label: 'Search', href: '/search', icon: 'Search' }],
  },
];

function isActive(href: string, path: string): boolean {
  if (href === '/') return path === '/';
  if (href === '/portfolio') {
    return path.startsWith('/portfolio') || path.startsWith('/entity/');
  }
  return path === href || path.startsWith(href + '/');
}

interface SidebarProps {
  clerk: Clerk | null;
  tenantName?: string | null;
}

export function Sidebar({ clerk, tenantName }: SidebarProps) {
  const { path } = useLocation();

  return (
    <aside class="sidebar">
      <div class="sidebar-brand">
        <div class="sidebar-brand-mark">K</div>
        <div class="sidebar-brand-name">Known Accounts</div>
      </div>

      <a class="sidebar-search" href="/search">
        <Icons.Search size={14} />
        <span>Search…</span>
        <span class="kbd">
          <span class="kbd-chip">⌘</span>
          <span class="kbd-chip">K</span>
        </span>
      </a>

      <div class="sidebar-groups">
        {NAV_GROUPS.map((g) => (
          <div class="sidebar-group" key={g.label}>
            <div class="sidebar-group-label">{g.label}</div>
            {g.items.map((it) => {
              const Icon = Icons[it.icon];
              const active = isActive(it.href, path);
              return (
                <a href={it.href} class={`nav-item ${active ? 'active' : ''}`} key={it.id}>
                  <Icon size={15} className="ico" />
                  <span>{it.label}</span>
                </a>
              );
            })}
          </div>
        ))}
      </div>

      <div class="sidebar-foot">
        <a
          href="/settings/advanced"
          class={`nav-item ${isActive('/settings', path) ? 'active' : ''}`}
        >
          <Icons.Settings size={14} className="ico" />
          <span>Settings</span>
        </a>
        {tenantName && (
          <div class="nav-item" style={{ pointerEvents: 'none', opacity: 0.7 }}>
            <Icons.Building size={14} className="ico" />
            <span class="truncate">{tenantName}</span>
          </div>
        )}
        <button
          class="nav-item"
          onClick={() => clerk?.signOut(() => window.location.reload())}
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
          }}
        >
          <Icons.LogOut size={14} className="ico" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Topbar — breadcrumb + Live indicator
// ───────────────────────────────────────────────────────────────────────────

function crumbsFor(path: string): string[] {
  if (path === '/') return ['Briefing'];
  if (path.startsWith('/entity/')) return ['Portfolio', 'Detail'];
  const map: Record<string, string[]> = {
    '/capture': ['Capture'],
    '/board': ['Board'],
    '/portfolio': ['Portfolio'],
    '/search': ['Search'],
  };
  return map[path] || ['Known Accounts'];
}

export function Topbar({ crumbs }: { crumbs?: string[] }) {
  const { path } = useLocation();
  const cr = crumbs ?? crumbsFor(path);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div class="topbar">
      <div class="topbar-breadcrumb">
        {cr.map((c, i) => (
          <>
            {i > 0 && <span style={{ color: 'var(--text-faint)' }}>/</span>}
            {i === cr.length - 1 ? <b>{c}</b> : <span>{c}</span>}
          </>
        ))}
      </div>
      <div class="topbar-spacer" />
      <div class="topbar-actions">
        <span class="topbar-sync" title={now.toLocaleTimeString()}>
          <span class="dot" />
          <span>Live</span>
        </span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Mobile tab bar — bottom-fixed, thumb-reachable. Hidden on desktop via
// the CSS @media query in chrome.css.
// ───────────────────────────────────────────────────────────────────────────

const TAB_ITEMS: NavItem[] = [
  { id: 'briefing', label: 'Briefing', href: '/', icon: 'Briefing' },
  { id: 'capture', label: 'Capture', href: '/capture', icon: 'Plus' },
  { id: 'board', label: 'Board', href: '/board', icon: 'Board' },
  { id: 'portfolio', label: 'Portfolio', href: '/portfolio', icon: 'Accounts' },
  { id: 'search', label: 'Search', href: '/search', icon: 'Search' },
];

export function MobileTabbar() {
  const { path } = useLocation();
  return (
    <nav class="tabbar">
      <div class="tabbar-inner">
        {TAB_ITEMS.map((it) => {
          const Icon = Icons[it.icon];
          const active = isActive(it.href, path);
          return (
            <a href={it.href} class={`tab-btn ${active ? 'active' : ''}`} key={it.id}>
              <Icon size={20} className="ico" />
              <span>{it.label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
